import { useState, type ReactNode } from "react";
import { Box, Chip, InputBase, Popover, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { CustomButton } from "@/components/common/customButton";
import { CustomMultiSelect } from "@/components/common/inputFields/customMultiSelect";
import { CustomSingleSelect } from "@/components/common/inputFields/customSingleSelect";
import searchIcon from "@/assets/svg/search.svg";
import type { Status } from "@/types/deal";

type Id = string | number;
type Option = { id: Id } & Record<string, unknown>;

export type PipelineFilterBarProps = {
  filter: string;
  setFilter: (value: string) => void;
  statusFilter: Status[];
  setStatusFilter: (statuses: Status[]) => void;
  statusOptions: Option[];
  countryFilterIds: Id[];
  setCountryFilterIds: (ids: Id[]) => void;
  countries: Option[];
  sectorFilterIds: Id[];
  setSectorFilterIds: (ids: Id[]) => void;
  sectors: Option[];
  capitalSourceFilterIds: Id[];
  setCapitalSourceFilterIds: (ids: Id[]) => void;
  capitalSources: Option[];
  activeOnly: boolean;
  getAllDeals: (activeOnly: boolean) => void;
  myDealsOnly: boolean;
  setMyDealsOnly: (value: boolean) => void;
};

/** "IC Memo, Term Sheet +1" */
function summariseSelection(labels: string[], max = 2): string {
  if (labels.length <= max) return labels.join(", ");
  return `${labels.slice(0, max).join(", ")} +${labels.length - max}`;
}

const labelsFor = (ids: Id[], options: Option[], key: string) =>
  ids.map((id) => {
    const found = options.find((o) => String(o.id) === String(id));
    return found ? String(found[key] ?? id) : String(id);
  });

const SCOPE_OPTIONS = [
  { id: "active", label: "Active deals" },
  { id: "all", label: "All deals" },
];

/** Same font, height, border and radius as the app's selects. */
const fieldSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  height: 32,
  px: 1.25,
  width: 240,
  border: "1px solid #C4C4C4",
  borderRadius: "8px",
  backgroundColor: "#fff",
  "&:hover": { borderColor: "#212121" },
  "&:focus-within": { borderColor: "#245EA0" },
} as const;

/**
 * Search, one Filters popover with removable chips for what's applied, and
 * the deal scope; built from the app's standard buttons and selects.
 */
export function PipelineFilterBar(props: PipelineFilterBarProps) {
  const {
    filter,
    setFilter,
    statusFilter,
    setStatusFilter,
    statusOptions,
    countryFilterIds,
    setCountryFilterIds,
    countries,
    sectorFilterIds,
    setSectorFilterIds,
    sectors,
    capitalSourceFilterIds,
    setCapitalSourceFilterIds,
    capitalSources,
    activeOnly,
    getAllDeals,
    myDealsOnly,
    setMyDealsOnly,
  } = props;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const chips = [
    statusFilter.length > 0 && {
      key: "status",
      label: `Status: ${summariseSelection(statusFilter.map((s) => s.status))}`,
      clear: () => setStatusFilter([]),
    },
    countryFilterIds.length > 0 && {
      key: "countries",
      label: `Country: ${summariseSelection(labelsFor(countryFilterIds, countries, "nicename"))}`,
      clear: () => setCountryFilterIds([]),
    },
    sectorFilterIds.length > 0 && {
      key: "sectors",
      label: `Sector: ${summariseSelection(labelsFor(sectorFilterIds, sectors, "sector"))}`,
      clear: () => setSectorFilterIds([]),
    },
    capitalSourceFilterIds.length > 0 && {
      key: "capital",
      label: `Capital: ${summariseSelection(labelsFor(capitalSourceFilterIds, capitalSources, "name"))}`,
      clear: () => setCapitalSourceFilterIds([]),
    },
  ].filter(Boolean) as Array<{ key: string; label: string; clear: () => void }>;

  const clearAll = () => chips.forEach((chip) => chip.clear());

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        flexWrap: "wrap",
        fontFamily: '"Helvetica Neue", sans-serif',
      }}
    >
      <Box sx={fieldSx}>
        <Box
          component="img"
          src={searchIcon}
          alt=""
          sx={{ width: 14, height: 14, opacity: 0.6 }}
        />
        <InputBase
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search deals"
          inputProps={{ "aria-label": "Search deals" }}
          sx={{
            flex: 1,
            fontSize: 12,
            fontFamily: "inherit",
            color: "#182737",
          }}
        />
        {filter && (
          <Close
            role="button"
            aria-label="Clear search"
            onClick={() => setFilter("")}
            sx={{ fontSize: 16, color: "#8E8E93", cursor: "pointer" }}
          />
        )}
      </Box>

      <Box
        onClick={(e) => setAnchor(e.currentTarget as HTMLElement)}
        sx={{ display: "inline-flex" }}
      >
        <CustomButton
          handleButtonPress={() => undefined}
          isButtonDisabled={false}
          text={chips.length > 0 ? `Filters · ${chips.length}` : "Filters"}
          solid={false}
          heightOverride="32px"
        />
      </Box>

      {chips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          onDelete={chip.clear}
          sx={{
            height: 28,
            borderRadius: "100px",
            fontSize: 12,
            fontFamily: "inherit",
            color: "#182737",
            backgroundColor: "rgba(27, 122, 99, 0.1)",
            "& .MuiChip-deleteIcon": {
              fontSize: 16,
              color: "#1B7A63",
              "&:hover": { color: "#00745e" },
            },
          }}
        />
      ))}
      {chips.length > 1 && (
        <Typography
          component="button"
          type="button"
          onClick={clearAll}
          sx={{
            border: 0,
            background: "none",
            cursor: "pointer",
            fontSize: 12,
            fontFamily: "inherit",
            color: "#1B7A63",
            textDecoration: "underline",
            p: 0,
          }}
        >
          Clear all
        </Typography>
      )}

      <Box sx={{ flex: 1 }} />

      <CustomButton
        handleButtonPress={() => setMyDealsOnly(!myDealsOnly)}
        isButtonDisabled={false}
        text="My deals"
        solid={myDealsOnly}
        heightOverride="32px"
      />
      {/* The select fills its parent, so give it a fixed width here. */}
      <Box sx={{ width: 140, flexShrink: 0 }}>
        <CustomSingleSelect
          value={activeOnly ? "active" : "all"}
          onChange={(e) => {
            const nextActiveOnly = e.target.value === "active";
            if (nextActiveOnly !== activeOnly) getAllDeals(nextActiveOnly);
          }}
          options={SCOPE_OPTIONS}
          labelKey="label"
          minWidth="130px"
        />
      </Box>

      <Popover
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { mt: 0.75, p: 2, width: 320, borderRadius: "12px" } },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <FilterField label="Status">
            <CustomMultiSelect
              fullWidth
              value={statusFilter.map((s) => s.id)}
              onChange={(e) => {
                const ids = e.target.value as Id[];
                setStatusFilter(
                  statusOptions.filter((opt): opt is Option & Status =>
                    ids.includes(opt.id),
                  ),
                );
              }}
              options={statusOptions}
              placeholder="Any status"
              labelKey="status"
            />
          </FilterField>
          <FilterField label="Country">
            <CustomMultiSelect
              fullWidth
              value={countryFilterIds}
              onChange={(e) => setCountryFilterIds(e.target.value as Id[])}
              options={countries}
              placeholder="Any country"
              labelKey="nicename"
            />
          </FilterField>
          <FilterField label="Sector">
            <CustomMultiSelect
              fullWidth
              value={sectorFilterIds}
              onChange={(e) => setSectorFilterIds(e.target.value as Id[])}
              options={sectors}
              placeholder="Any sector"
              labelKey="sector"
            />
          </FilterField>
          <FilterField label="Capital source">
            <CustomMultiSelect
              fullWidth
              value={capitalSourceFilterIds}
              onChange={(e) =>
                setCapitalSourceFilterIds(e.target.value as Id[])
              }
              options={capitalSources}
              placeholder="Any capital source"
              labelKey="name"
            />
          </FilterField>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 0.5,
            }}
          >
            <CustomButton
              handleButtonPress={clearAll}
              isButtonDisabled={chips.length === 0}
              text="Clear all"
              solid={false}
              heightOverride="32px"
            />
            <CustomButton
              handleButtonPress={() => setAnchor(null)}
              isButtonDisabled={false}
              text="Done"
              heightOverride="32px"
            />
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Box>
      <Typography
        sx={{
          mb: 0.5,
          fontSize: 12,
          fontFamily: '"Helvetica Neue", sans-serif',
          color: "#344054",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}
